import { useCallback, useEffect, useMemo, useState } from "react";
import { addDays, differenceInCalendarDays, format } from "date-fns";
import { supabase } from "../../lib/supabase";
import type { Deal } from "../../lib/types";
import UnableToFlyModal from "./actions/UnableToFlyModal";
import WeatherReportCell from "./WeatherReportCell";
import RouteBuilder from "./RouteBuilder";

import DealLink from "../DealLink";
import DealAgeChips from "../shared/DealAgeChips";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
type Urgency = "overdue" | "today" | "week" | "none";

const URGENCY_BG: Record<Urgency, string> = {
  overdue: "border-l-4 border-red-500 bg-red-50",
  today: "border-l-4 border-orange-500 bg-orange-50",
  week: "border-l-4 border-yellow-400 bg-yellow-50",
  none: "border-l-4 border-slate-200 bg-white",
};

const URGENCY_RANK: Record<Urgency, number> = {
  overdue: 0,
  today: 1,
  week: 2,
  none: 3,
};

type Row = {
  deal: Deal;
  slaTarget: Date;
  slaDays: number;
  urgency: Urgency;
  label: string;
};

function formatAddress(d: Deal) {
  return [
    d.property_street,
    [d.property_city, d.property_state].filter(Boolean).join(", "),
    d.property_zip,
  ]
    .filter(Boolean)
    .join(" ");
}

function classify(deal: Deal, today: Date): Row {
  // 3-day drone SLA from appointment_verified_at (preferred) or appointment_date
  const base =
    deal.appointment_verified_at ??
    deal.appointment_date ??
    null;
  const baseDate = base ? new Date(base) : null;
  const slaTarget = baseDate ? addDays(baseDate, 3) : addDays(today, 14);
  const days = differenceInCalendarDays(today, slaTarget);
  let urgency: Urgency = "none";
  if (days > 0) urgency = "overdue";
  else if (days === 0) urgency = "today";
  else if (days >= -3) urgency = "week";
  const label =
    days > 0
      ? `SLA overdue ${days} day${days === 1 ? "" : "s"}`
      : days === 0
        ? "SLA due today"
        : `SLA in ${-days} day${-days === 1 ? "" : "s"}`;
  return { deal, slaTarget, slaDays: days, urgency, label };
}

export default function QueueTab() {
  const today = useMemo(() => new Date(), []);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [unableFor, setUnableFor] = useState<Deal | null>(null);
  const [skipBusy, setSkipBusy] = useState<Record<string, boolean>>({});
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Selection state for route builder
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showRouteBuilder, setShowRouteBuilder] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      // Stage is the source of truth — pull every appointment_received deal,
      // even ones that have an orphaned drone_scheduled_date set (e.g. an
      // older scheduling path that wrote the date but didn't transition the
      // stage). We surface the stale date on the card so Sam can clean it up.
      const { data, error } = await supabase
        .from("deals")
        .select(
          "id,homeowner_name,homeowner_phone,property_street,property_city,property_state,property_zip,property_lat,property_lon,claim_number,stage,phase,appointment_date,appointment_verified,appointment_verified_at,assigned_drone_pilot_id,drone_scheduled_date,drone_unable_to_fly,owner_id,days_in_stage,stage_entered_at,phase_entered_at,created_at,salesrabbit_lead_id,sr_weather_report_generated,sr_weather_report_storage_path,sr_weather_report_file_name,sr_weather_report_file_size",
        )
        .eq("stage", "appointment_received");
      if (error) {
        if (!cancelled) setError(error.message);
        return;
      }
      const classified = ((data ?? []) as Deal[]).map((d) =>
        classify(d, today),
      );
      classified.sort((a, b) => {
        const byUrgency = URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency];
        if (byUrgency !== 0) return byUrgency;
        return b.slaDays - a.slaDays;
      });
      if (!cancelled) {
        setRows(classified);
        setSelected(new Set()); // clear selection on reload
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [today, reloadKey]);

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (!rows) return;
    setSelected((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.deal.id));
    });
  }, [rows]);

  const selectedDeals = useMemo(
    () =>
      rows
        ? rows.filter((r) => selected.has(r.deal.id)).map((r) => r.deal)
        : [],
    [rows, selected],
  );

  if (error)
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded">
        Couldn't load queue — {error}
      </div>
    );
  if (!rows)
    return <div className="text-slate-500 p-4">Loading queue…</div>;

  const counts = rows.reduce(
    (acc, r) => ({ ...acc, [r.urgency]: (acc[r.urgency] ?? 0) + 1 }),
    {} as Record<Urgency, number>,
  );

  const allSelected = rows.length > 0 && selected.size === rows.length;

  return (
    <div className="pb-20">
      {/* Urgency counts */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="px-2 py-1 rounded bg-red-100 text-red-700">
          Overdue: {counts.overdue ?? 0}
        </span>
        <span className="px-2 py-1 rounded bg-orange-100 text-orange-700">
          Due today: {counts.today ?? 0}
        </span>
        <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-700">
          This week: {counts.week ?? 0}
        </span>
        <span className="px-2 py-1 rounded bg-slate-100 text-slate-600">
          No urgency: {counts.none ?? 0}
        </span>

        {/* Select All toggle */}
        {rows.length > 0 && (
          <button
            onClick={toggleAll}
            className="ml-auto px-3 py-1 rounded text-xs font-medium bg-bardin-ink text-white hover:bg-bardin-ink/80"
          >
            {allSelected ? "Deselect all" : "Select all"}
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-slate-500">
          No drone-ready appointments. Nothing in the queue.
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const isSelected = selected.has(r.deal.id);
            return (
              <li
                key={r.deal.id}
                className={
                  "rounded-lg shadow-sm px-4 py-3 transition-all " +
                  URGENCY_BG[r.urgency] +
                  (isSelected
                    ? " ring-2 ring-bardin-orange ring-offset-1"
                    : "")
                }
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="flex gap-3 items-start flex-1">
                    {/* Checkbox */}
                    <label className="flex items-center mt-1 shrink-0 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(r.deal.id)}
                        className="w-5 h-5 rounded border-slate-300 text-bardin-orange focus:ring-bardin-orange cursor-pointer"
                      />
                    </label>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium px-2 py-0.5 rounded bg-white shadow-sm">
                          {r.label}
                        </span>
                        <span className="text-xs text-slate-500">
                          Target: {format(r.slaTarget, "MMM d")}
                        </span>
                        {r.deal.claim_number && (
                          <span className="text-xs text-slate-400">
                            #{r.deal.claim_number}
                          </span>
                        )}
                        {r.deal.drone_scheduled_date && (
                          <span
                            className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800"
                            title="This deal has a drone date set but stayed in the queue. Re-route it (or use Send back to queue from the scheduled tab) to clean up."
                          >
                            Stale fly date:{" "}
                            {format(new Date(r.deal.drone_scheduled_date), "MMM d")}
                          </span>
                        )}
                      </div>
                      <div className="font-semibold text-bardin-ink mt-0.5">
                        <DealLink id={r.deal.id}>
                          {r.deal.homeowner_name ?? "(no name)"}
                        </DealLink>
                      </div>
                      <div className="text-sm text-slate-600">
                        {isAddressLinkable(r.deal) ? (
                          <a
                            href={mapsHref(r.deal)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline hover:text-slate-800"
                          >
                            {formatAddress(r.deal)}
                          </a>
                        ) : (
                          formatAddress(r.deal) || "(no address)"
                        )}
                      </div>
                      <DealAgeChips deal={r.deal} className="mt-1" />
                      <div className="mt-2">
                        <WeatherReportCell
                          dealId={r.deal.id}
                          salesrabbitLeadId={r.deal.salesrabbit_lead_id}
                          storagePath={r.deal.sr_weather_report_storage_path}
                          fileName={r.deal.sr_weather_report_file_name}
                          fileSize={r.deal.sr_weather_report_file_size}
                          generated={r.deal.sr_weather_report_generated}
                          onUpdated={reload}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 shrink-0 ml-8 md:ml-0">
                    <button
                      onClick={async () => {
                        if (skipBusy[r.deal.id]) return;
                        if (!window.confirm(`Skip drone and send "${r.deal.homeowner_name ?? "(no name)"}" straight to Initial Contact?`)) return;
                        setSkipBusy((b) => ({ ...b, [r.deal.id]: true }));
                        try {
                          const { error: e } = await supabase
                            .from("deals")
                            .update({ stage: "initial_contact", phase: "leads", days_in_stage: 0 })
                            .eq("id", r.deal.id);
                          if (e) throw e;
                          reload();
                        } catch (e: any) {
                          alert(`Update failed: ${e.message ?? e}`);
                        } finally {
                          setSkipBusy((b) => ({ ...b, [r.deal.id]: false }));
                        }
                      }}
                      disabled={!!skipBusy[r.deal.id]}
                      className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      {skipBusy[r.deal.id] ? "Moving…" : "No Drone → Lead"}
                    </button>
                    <button
                      onClick={() => setUnableFor(r.deal)}
                      className="px-3 py-1.5 rounded bg-amber-100 text-amber-800 text-sm hover:bg-amber-200"
                    >
                      Unable to fly
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Sticky bottom bar — visible when 1+ deals selected */}
      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-bardin-ink text-white px-4 py-3 shadow-lg flex items-center justify-between">
          <div className="text-sm">
            <span className="font-semibold">{selected.size}</span>{" "}
            stop{selected.size !== 1 ? "s" : ""} selected
            <span className="text-slate-400 ml-2">
              · est {Math.floor((selected.size * 17) / 60) > 0
                ? `${Math.floor((selected.size * 17) / 60)}h `
                : ""}
              {(selected.size * 17) % 60}m
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 rounded text-sm text-slate-300 hover:text-white"
            >
              Clear
            </button>
            <button
              onClick={() => setShowRouteBuilder(true)}
              className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90"
            >
              Build Route ({selected.size})
            </button>
          </div>
        </div>
      )}

      {/* Route builder full-screen panel */}
      {showRouteBuilder && (
        <RouteBuilder
          deals={selectedDeals}
          onClose={() => setShowRouteBuilder(false)}
          onScheduled={() => {
            setSelected(new Set());
            reload();
          }}
        />
      )}

      {unableFor && (
        <UnableToFlyModal
          open
          deal={unableFor}
          onClose={() => setUnableFor(null)}
          onCompleted={reload}
        />
      )}
    </div>
  );
}
